import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import CountryFlag from "../../common/CountryFlag";
import { SearchIcon, SelectArrowIcon } from "../../icons";
import { cn } from "../../../utils/classnames";
import useCountries from "./useCountries";

/**
 * Country dial-code picker shown at the start of a phone-number field.
 *
 * Closed: flag + dial code ("🇯🇵 +81 ▾").
 * Open:   a popover with a search box and the country list. Works with the
 *         mouse and the keyboard (↑ ↓ to move, Enter to pick, Esc to close).
 *
 * `onSelect(dialCode, countryCode, countryName)` — same arguments as the
 * Bootstrap dropdown it replaces, so existing handlers work unchanged.
 *
 * `lng` (optional) pins the picker's texts to one language. The admin
 * dashboard passes "en": it has no /en|jp/ URL segment, so the site language
 * would otherwise be whatever the admin last browsed the front pages in.
 */

const POPOVER_HEIGHT = 340; // used to decide whether to open upwards

const popoverMotion = (opensUpward, shouldReduce) => ({
    initial: { opacity: 0, y: shouldReduce ? 0 : opensUpward ? 6 : -6, scale: shouldReduce ? 1 : 0.98 },
    animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: shouldReduce ? 0 : opensUpward ? 4 : -4, transition: { duration: 0.14 } },
});

const matchesSearch = (country, searchText) => {
    const search = searchText.trim().toLowerCase();
    if (!search) return true;
    return (
        country.name.toLowerCase().includes(search) ||
        country.dial_code.includes(search) ||
        country.code.toLowerCase() === search
    );
};

// Scrolls only the list (never the page) so a country's row is visible.
// "center" puts it mid-list; "nearest" moves just enough to reveal it.
const scrollListToRow = (listElement, countryCode, position) => {
    const row = listElement?.querySelector(`[data-country-code="${countryCode}"]`);
    if (!row) return;
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (position === "center") {
        listElement.scrollTop = rowTop - listElement.clientHeight / 2 + row.offsetHeight / 2;
    } else if (rowTop < listElement.scrollTop) {
        listElement.scrollTop = rowTop;
    } else if (rowBottom > listElement.scrollTop + listElement.clientHeight) {
        listElement.scrollTop = rowBottom - listElement.clientHeight;
    }
};

// Closes the popover when the user clicks or taps anywhere outside it.
const useClickOutside = (elementRef, isActive, onOutsideClick) => {
    useEffect(() => {
        if (!isActive) return undefined;
        const handlePointerDown = (event) => {
            if (elementRef.current && !elementRef.current.contains(event.target)) onOutsideClick();
        };
        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [elementRef, isActive, onOutsideClick]);
};

const CountryOption = ({ country, isSelected, isActive, onPick, onHover }) => (
    <li role="option" aria-selected={isSelected}>
        <button
            type="button"
            data-country-code={country.code}
            onClick={() => onPick(country)}
            onMouseMove={onHover}
            className={cn(
                "tw:flex tw:w-full tw:items-center tw:gap-3 tw:px-4 tw:py-2.5 tw:text-left tw:text-[14px] tw:text-[#282828] tw:transition-colors",
                isActive && "tw:bg-[#FBF6EA]",
                isSelected && "tw:font-semibold"
            )}
        >
            <span className="tw:flex tw:h-[18px] tw:w-6 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-[3px] tw:shadow-[0_0_0_1px_rgba(0,0,0,0.06)]">
                <CountryFlag code={country.code} emoji={country.flag} alt={country.name} />
            </span>
            <span className="tw:flex-1 tw:truncate">{country.name}</span>
            <span className="tw:tabular-nums tw:text-[13px] tw:text-[#8D8D8D]">{country.dial_code}</span>
            <span className="tw:w-3 tw:text-[#DBBE78]" aria-hidden="true">
                {isSelected ? "✓" : ""}
            </span>
        </button>
    </li>
);

const CountryCodePicker = ({ countryCode, dialCode, onSelect, disabled = false, lng }) => {
    const { t: tInSiteLanguage, i18n } = useTranslation();
    const t = lng ? i18n.getFixedT(lng) : tInSiteLanguage;
    const shouldReduce = useReducedMotion();
    const countries = useCountries();

    const [isOpen, setIsOpen] = useState(false);
    const [opensUpward, setOpensUpward] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);

    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const searchRef = useRef(null);
    const listRef = useRef(null);

    const selectedCountry = countries.find((country) => country.code === countryCode);
    const visibleCountries = useMemo(
        () => countries.filter((country) => matchesSearch(country, searchText)),
        [countries, searchText]
    );

    const close = useCallback(() => setIsOpen(false), []);
    useClickOutside(wrapperRef, isOpen, close);

    const open = () => {
        if (disabled) return;
        // Open upwards when the field sits too close to the bottom of the screen.
        const triggerBox = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - triggerBox.bottom;
        setOpensUpward(spaceBelow < POPOVER_HEIGHT && triggerBox.top > spaceBelow);
        setSearchText("");
        setIsOpen(true);
    };

    // On open: focus the search box and start on the selected country, shown
    // in the middle of the list. preventScroll / scrolling the list by hand
    // (not scrollIntoView) so the page itself never jumps.
    useEffect(() => {
        if (!isOpen) return;
        const selectedIndex = countries.findIndex((country) => country.code === countryCode);
        setActiveIndex(Math.max(selectedIndex, 0));
        searchRef.current?.focus({ preventScroll: true });
        scrollListToRow(listRef.current, countryCode, "center");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Typing a search restarts the highlight at the first match.
    useEffect(() => setActiveIndex(0), [searchText]);

    // Keep the highlighted row visible while moving with the arrow keys.
    useEffect(() => {
        if (!isOpen) return;
        const activeCountry = visibleCountries[activeIndex];
        if (activeCountry) scrollListToRow(listRef.current, activeCountry.code, "nearest");
    }, [activeIndex, isOpen, visibleCountries]);

    const pick = (country) => {
        onSelect(country.dial_code, country.code, country.name);
        close();
        triggerRef.current?.focus();
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, visibleCountries.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === "Enter") {
            event.preventDefault(); // never submit the surrounding form
            if (visibleCountries[activeIndex]) pick(visibleCountries[activeIndex]);
        } else if (event.key === "Escape") {
            close();
            triggerRef.current?.focus();
        }
    };

    // The wrapper is always `relative`: the popover is anchored to it, including
    // while it fades out after closing — otherwise it jumps to another ancestor.
    return (
        <div ref={wrapperRef} className="tw:relative tw:flex tw:rounded-l-[inherit]">
            <button
                ref={triggerRef}
                type="button"
                onClick={() => (isOpen ? close() : open())}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                className="tw:flex tw:h-full tw:items-center tw:gap-2 tw:rounded-l-[inherit] tw:border-r tw:border-[#EFE3C4] tw:bg-[#FBF6EA] tw:pl-3 tw:pr-2.5 tw:text-[14px] tw:text-[#282828] tw:outline-none tw:transition-colors tw:hover:bg-[#F7EDD5] tw:focus-visible:bg-[#F7EDD5] tw:focus-visible:shadow-[inset_0_0_0_2px_rgba(219,190,120,0.7)] tw:disabled:cursor-not-allowed tw:disabled:opacity-60"
            >
                <span className="tw:flex tw:h-[18px] tw:w-6 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-[3px] tw:shadow-[0_0_0_1px_rgba(0,0,0,0.06)]">
                    <CountryFlag code={countryCode} emoji={selectedCountry?.flag} alt={selectedCountry?.name} />
                </span>
                <span className="tw:tabular-nums">{dialCode}</span>
                <motion.span
                    className="tw:flex"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    aria-hidden="true"
                >
                    <SelectArrowIcon color="#B8995A" size={9} />
                </motion.span>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        {...popoverMotion(opensUpward, shouldReduce)}
                        style={{ transformOrigin: opensUpward ? "bottom left" : "top left" }}
                        className={cn(
                            "tw:absolute tw:left-0 tw:z-50 tw:w-[min(360px,calc(100vw-2rem))] tw:overflow-hidden tw:rounded-lg tw:border tw:border-[#EFE6D2] tw:border-t-2 tw:border-t-[#DBBE78] tw:bg-white tw:shadow-[0_18px_40px_-12px_rgba(40,40,40,0.28)]",
                            opensUpward ? "tw:bottom-full tw:mb-2" : "tw:top-full tw:mt-2"
                        )}
                    >
                        <div className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-[#F1F1F1] tw:px-4">
                            <SearchIcon size={13} color="#B8995A" />
                            <input
                                ref={searchRef}
                                type="text"
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder={t("SelectCountry.Placeholder")}
                                aria-label={t("SelectCountry.Placeholder")}
                                className="tw:w-full tw:border-0 tw:bg-transparent tw:py-3 tw:text-[14px] tw:text-[#282828] tw:outline-none tw:placeholder:text-[#A7A7A7]"
                            />
                        </div>

                        <ul ref={listRef} role="listbox" className="tw:relative tw:!m-0 tw:max-h-[260px] tw:list-none tw:overflow-y-auto tw:!px-0 tw:!py-1 country-list-scroll">
                            {visibleCountries.map((country, index) => (
                                <CountryOption
                                    key={country.code}
                                    country={country}
                                    isSelected={country.code === countryCode}
                                    isActive={index === activeIndex}
                                    onPick={pick}
                                    onHover={() => setActiveIndex(index)}
                                />
                            ))}
                            {visibleCountries.length === 0 && (
                                <li className="tw:px-4 tw:py-6 tw:text-center tw:text-[13px] tw:text-[#8D8D8D]">
                                    {t("SelectCountry.NoResults")}
                                </li>
                            )}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CountryCodePicker;
